import { useMemo, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { feedbackExt } from '@/lib/service';

export default function ExtesionUninstall() {
  const { t } = useTranslation();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const UNINSTALL_REASONS = useMemo(
    () => [
      { key: "It wasn't working as expected.", value: t("It wasn't working as expected.") },
      { key: 'I no longer need the tool.', value: t('I no longer need the tool.') },
      { key: 'I experienced technical issues or bugs.', value: t('I experienced technical issues or bugs.') },
      { key: 'I found another solution.', value: t('I found another solution.') },
      { key: "The features didn't meet my needs.", value: t("The features didn't meet my needs.") },
      { key: "I'm not using Instagram anymore.", value: t("I'm not using Instagram anymore.") },
    ],
    [t]
  );

  const toggleReason = (reason: { key: string; value: string }) => {
    setSelectedReasons((prev) => {
      if (prev.includes(reason.key)) {
        // 取消选中
        return prev.filter((r) => r !== reason.key);
      } else {
        // 添加选中项,并按 UNINSTALL_REASONS 的顺序排序
        const newSelected = [...prev, reason.key];
        return newSelected.sort((a, b) => {
          const indexA = UNINSTALL_REASONS.findIndex((r) => r.key === a);
          const indexB = UNINSTALL_REASONS.findIndex((r) => r.key === b);
          return indexA - indexB;
        });
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedReasons.length === 0) {
      message.warning('Please select at least one reason');
      return;
    }
    setLoading(true);
    try {
      await feedbackExt(selectedReasons);
      message.success('Feedback submitted. Thanks for helping us improve!');
      setTimeout(() => {
        router.replace('/ig-follower-export-tool');
      }, 1000);
    } catch (error) {
      message.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#f8f9ff] to-white overflow-hidden flex flex-col items-center px-5">
      {/* Background decorative image */}
      <div className="absolute top-[-443px] left-1/2 -translate-x-1/2 w-[2367px] h-[2179px] pointer-events-none z-0">
        <div className="absolute inset-0 opacity-50">{/* Placeholder for background decoration */}</div>
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center w-full max-w-[1200px] z-[1] pt-[101px] lg:pt-[220px]">
        {/* Title */}
        <h1 className="font-inter font-bold text-[42px] leading-[1.4] tracking-[2.1px] text-[#0c0c0c] text-center mb-6 max-md:text-[32px] max-md:tracking-[1.6px]">
          {t('We Value Your Feedback')}
        </h1>

        {/* Description */}
        <p className="font-inter font-medium text-[16px] leading-[1.4] tracking-[0.8px] text-[#0c0c0c] text-center max-w-[889px] mb-[125px] max-md:text-[14px] max-md:mb-[60px]">
          {t(
            "We regret that you've decided to uninstall the extension. However, you can still enjoy all the features via our web platform. Please visit DolphinRadar's IG Follower Export Tool."
          )}
        </p>

        {/* Form Card */}
        <div className="bg-white rounded-[12px] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.15)] w-full max-w-[838px] p-9 mb-[100px] max-md:p-6">
          {/* Form Title */}
          <h2 className="font-inter font-medium text-[18px] leading-[1.4] text-[#0c0c0c]  mb-[30px] max-md:text-[16px] max-md:mb-8 text-left">
            {t('Why did you uninstall IG Follower Export Tool')}
          </h2>

          {/* Options List */}
          <div className="flex flex-col gap-4 mb-[78px]">
            {UNINSTALL_REASONS.map(({ key, value }, index) => (
              <div
                key={index}
                onClick={() => toggleReason({ key, value })}
                className="bg-[#fafbff] border border-[#dedee3] rounded-[6px] min-h-[46px] flex items-center px-4 py-3 cursor-pointer transition-all hover:border-[#725cff]"
              >
                {/* Checkbox */}
                <div
                  className={`w-4 h-4 border rounded-[3px] mr-4 flex-shrink-0 flex items-center justify-center transition-all ${
                    selectedReasons.includes(key) ? 'bg-[#725cff] border-[#725cff]' : 'bg-white border-[#d9d9d9]'
                  }`}
                >
                  {selectedReasons.includes(key) && (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      viewBox="0 0 10 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M2 5L4 7L8 3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                {/* Label */}
                <span className="font-inter font-normal text-[16px] leading-[1.93] text-[#0c0c0c]">{value}</span>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#725cff] rounded-[8px] h-[42px] flex items-center justify-center cursor-pointer transition-all hover:bg-[#5d47e5] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed border-none w-[348px] mx-auto max-md:w-full"
          >
            <span className="font-inter font-semibold text-[14px] leading-[20px] text-white">
              {loading ? t('Submitting...') : t('Submit')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
